import { useEffect, useState, type ReactNode } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import {
  getOrder,
  getOrderedMaterials,
  getTimesheet,
} from "@/lib/projectOverview";
import type { NoteRefType } from "@/projectOverview/notes/types";
import { PROJECT_OVERVIEW_NESTED_OVERLAY_Z } from "@/projectOverview/projectOverviewLayers";

export function NoteReferenceModal({
  open,
  orderId,
  refType,
  refId,
  refLabel,
  onClose,
}: {
  open: boolean;
  orderId: number;
  refType: NoteRefType | string;
  refId: string;
  refLabel: string;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [body, setBody] = useState<ReactNode>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setBody(<p className="text-sm text-label-secondary">Lade …</p>);

    void (async () => {
      try {
        let content: ReactNode;
        if (refType === "auftrag") {
          const item = await getOrder(Number(refId));
          content = (
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-label-secondary">Auftragsnummer</dt>
                <dd className="text-label">{item.auftragsnummer || "—"}</dd>
              </div>
              <div>
                <dt className="text-label-secondary">Kunde</dt>
                <dd className="text-label">{item.kunde || "—"}</dd>
              </div>
              <div>
                <dt className="text-label-secondary">Ort</dt>
                <dd className="text-label">{item.ort || "—"}</dd>
              </div>
              <div>
                <dt className="text-label-secondary">Beschreibung</dt>
                <dd className="whitespace-pre-wrap text-label">{item.beschreibung || "—"}</dd>
              </div>
            </dl>
          );
        } else if (refType === "timesheet") {
          const item = await getTimesheet(refId);
          content = (
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-label-secondary">Stundennachweis</dt>
                <dd className="text-label">
                  {item.display_number || item.timesheet_number || refId}
                </dd>
              </div>
              <div>
                <dt className="text-label-secondary">Status</dt>
                <dd className="text-label">{item.status || "—"}</dd>
              </div>
            </dl>
          );
        } else if (refType === "order") {
          const rows = await getOrderedMaterials(orderId);
          const matches = rows.filter((r) => String(r.bestellnummer || "") === String(refId));
          content = (
            <div className="text-sm">
              <p>
                <span className="text-label-secondary">Bestellnummer: </span>
                <span className="font-medium text-label">{refId}</span>
              </p>
              <p className="mt-2 font-medium text-label-secondary">Positionen</p>
              <ul className="mt-1 list-inside list-disc space-y-1 text-label">
                {matches.length ? (
                  matches.slice(0, 12).map((row, idx) => (
                    <li key={String(row.id ?? idx)}>{String(row.label || row.text || "Artikel")}</li>
                  ))
                ) : (
                  <li className="text-label-tertiary">Keine Positionen gefunden.</li>
                )}
              </ul>
            </div>
          );
        } else {
          content = (
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-label-secondary">Mitarbeiter</dt>
                <dd className="text-label">{refLabel || "—"}</dd>
              </div>
              <div>
                <dt className="text-label-secondary">Referenz</dt>
                <dd className="text-label">{refId || "—"}</dd>
              </div>
            </dl>
          );
        }
        if (!cancelled) setBody(content);
      } catch {
        if (!cancelled) {
          setBody(<p className="text-sm text-status-red">Details konnten nicht geladen werden.</p>);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, orderId, refType, refId, refLabel]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={refLabel || "Referenz"}
      size="md"
      zIndexClass={PROJECT_OVERVIEW_NESTED_OVERLAY_Z}
    >
      {loading ? (
        <div className="flex items-center gap-2">
          <Spinner />
          <span className="text-sm text-label-secondary">Lade …</span>
        </div>
      ) : (
        body
      )}
    </Dialog>
  );
}
